<template>
  <aw-modal
    :opened="opened"
    size="sm"
    :modal-title="modalTitle"
    :modal-title-attrs="{}"
    @close-modal="closeModal"
  >
    <template #content>
      <aw-separator margin-bottom="md" margin-top="none" />
      <div v-if="!isMultiple" :class="$style.product">
        <div :class="$style.productImageWrapper">
          <lv-image
            type="variant"
            :sources="productImage"
            sizing="contain"
            :class="$style.productImage"
            :alt="productName"
          />
        </div>

        <h3 :class="$style.productName">
          {{ productName }}
        </h3>
      </div>
      <div v-else :class="$style.productsImageContainerWrapper">
        <ul :class="[$style.productsImageContainer, 'ulReset', 'awScrollbar']">
          <li v-for="item in productsImages" :key="item.uuid">
            <div v-if="item.isPlaceholder" :class="$style.productsImageItemPlaceholder">
              {{ item.placeholderText }}
            </div>
            <div v-else :class="$style.productsImageItem">
              <aw-checkout-cart-item-image :item="item" />
            </div>
          </li>
        </ul>
      </div>

      <div v-if="showRemovedFromList" :class="$style.removeFromListTitle">
        {{ $awt('aw.common.favourite.mobile_modal.remove_from_list_title') }}
      </div>

      <template v-if="showRemovedFromList">
        <ul v-if="selectedShoppingLists.length" :class="['ulReset']">
          <li v-for="list in selectedShoppingLists" :key="list.id" :class="$style.removeFromListItem">
            <span>{{ list.name }}</span>
            <button
              type="button"
              class="buttonReset"
              :aria-label="$awt('aw.common.favourite.remove_label', {product_name: productName, list_name: list.name})"
              @click="removeFromList(list.id)"
            >
              <lv-icon name="trash-version2-16" :size="16" :class="$style.removeFromListItemIcon" />
            </button>
          </li>
        </ul>
        <div v-else :class="$style.emptyList">
          <img alt="" :src="$addWebpPostfixIfSupported({ url: abstract77, webpUrl: abstract77Webp })" loading="lazy">
        </div>

        <aw-separator margin-bottom="md" margin-top="md" />

        <div :class="$style.addShoppingListButtonWrapper">
          <lv-button
            styling="primary-dark"
            shrink
            :class="$style.addShoppingListButton"
            @click="closeModal"
          >
            {{ $awt('aw.common.favourite.mobile_modal.close') }}
          </lv-button>
        </div>
      </template>
      <template v-else>
        <div v-for="list in sortedShoppingLists" :key="list.id" :class="$style.shoppingListItem">
          <aw-checkbox-version2
            v-model="list.isSelected"
            has-label
            @update:model-value="handleCheckboxChange(list, $event)"
          >
            <template #label>
              {{ list.name }}
            </template>
          </aw-checkbox-version2>
        </div>

        <aw-add-new-shopping-list-form
          v-show="showAddNewShoppingListForm"
          ref="addNewShoppingListForm"
          display-type="tooltip"
          @close-add-new-shopping-list-form="showAddNewShoppingListForm = false"
        />

        <lv-button
          v-show="!showAddNewShoppingListForm"
          ref="addNewShoppingListButton"
          stretch
          styling="small-secondary-dark"
          :class="$style.addNewShoppingListButton"
          @click="showAddNewShoppingListFormCallback"
          @focus="$refs.addNewShoppingListForm.v$.$reset()"
        >
          {{ $awt('aw.common.favourite.create_new_list') }}
        </lv-button>

        <aw-separator margin-bottom="md" margin-top="md" />

        <div :class="$style.addShoppingListButtonWrapper">
          <lv-button
            styling="primary-dark"
            shrink
            :class="$style.addShoppingListButton"
            :disabled="showAddNewShoppingListForm"
            loading-id="save-add-to-saved-basket-modal"
            @click="saveChanges"
          >
            {{ $awt('aw.global.ok') }}
          </lv-button>
        </div>
      </template>
    </template>
  </aw-modal>
</template>

<script>
  import { mapState } from 'pinia';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';

  import { useModalStore } from '~~/common/stores/modal';
  import { useProductsStore } from '~~/common/stores/products';
  import { useUserStore } from '~~/common/stores/user';
  import AwModal from '~~/common/components/Common/AwModal';
  import AwCheckboxVersion2 from '~~/common/components/Common/Widgets/AwCheckboxVersion2';
  import { LvIcon, LvButton, LvImage } from '~~/common/components/loginet-vue-shop/index.mjs';
  import AwSeparator from '~~/common/components/Common/AwSeparator';
  import { setErrorNotification } from '~~/common/utils/helper';
  import { unitNames } from '~~/common/utils/variant';
  import { PUSH_ITEM_TO_FAVOURITE } from '~~/common/plugins/aw-analytics';
  import abstract77 from '~~/common/assets/images/abstract-77.png';
  import abstract77Webp from '~~/common/assets/images/abstract-77.png.webp';
  import listTypeEnum from '~~/shop/components/Page/Profile/SaveBasket/enum/listTypeEnum.js';
  import { useNuxtApp } from 'nuxt/app';
  import AwAddNewShoppingListForm from '../../Page/Common/AwAddNewShoppingListForm.vue';
  import AwCheckoutCartItemImage from '~~/shop/components/Page/Checkout/AwCheckoutCartItemImage.vue';

  export default {
    name: 'AwAddToSavedBasketModalVersion2',
    components: {
      AwAddNewShoppingListForm,
      LvImage,
      AwSeparator,
      LvIcon,
      LvButton,
      AwCheckboxVersion2,
      AwModal,
      AwCheckoutCartItemImage,
    },
    props: {
      modalType: {
        type: String,
        required: true,
      },
      selectedValue: {
        type: String,
        required: true,
      },
      count: {
        type: Number,
        required: true,
      },
      analyticsFavouriteGroupType: {
        type: Object,
        required: true,
        validator: (v) => {
          return v && typeof v === 'object' && ('checkoutType' in v || 'variantId' in v);
        },
      },
      shoppingListData: {
        type: Array,
        default: () => {
          return [];
        },
      },
      productName: {
        type: String,
        required: true,
      },
      productImage: {
        type: Array,
        required: true,
      },
      showRemovedFromList: {
        type: Boolean,
        required: true,
      },
      productId: {
        type: Number,
        required: true,
      },
      productData: {
        type: Object,
        required: true,
      },
      variantId: {
        type: Number,
        required: true,
      },
      isMultiple: {
        type: Boolean,
        default: false,
      },
      products: {
        type: Array,
        required: false,
        default: () => ([]),
      },
    },
    emits: ['modal-close'],
    data () {
      return {
        localShoppingListData: [],
        showAddNewShoppingListForm: false,
        abstract77,
        abstract77Webp,
      };
    },
    computed: {
      ...mapState(useModalStore, {
        modal: state => state.activeGenericModal,
      }),
      ...mapState(useUserStore, {
        shoppingLists: state => state.shoppingListNames,
      }),
      defaultShoppingListId () {
        return this.mappedShoppingLists.find(list => list.default)?.id;
      },
      mappedShoppingLists () {
        return this.shoppingLists?.map(shoppingList => ({
          id: shoppingList.id,
          name: shoppingList.listType === listTypeEnum.FAVOURITE ? this.$awt('aw.profile.saved_baskets.default_list.title') : shoppingList.name,
          default: shoppingList.listType === listTypeEnum.FAVOURITE,
          isOkauchan: shoppingList.listType === listTypeEnum.OKAUCHAN,
          isSelected: this.localShoppingListData.some(listItem => String(listItem) === String(shoppingList.id)),
        })) ?? [];
      },
      sortedShoppingLists () {
        // sort the mappedShoppingLists by default, isSelected, then name
        return [...(this.mappedShoppingLists ?? [])]
          .sort(({
                   default: aDefault,
                   isSelected: aSelected,
                   name: aName,
                   isOkauchan: aIsOkauchan,
                 },
                 {
                   default: bDefault,
                   isSelected: bSelected,
                   name: bName,
                   isOkauchan: bIsOkauchan,
                 }) =>
            bDefault - aDefault || bIsOkauchan - aIsOkauchan || bSelected - aSelected || aName.localeCompare(bName),
          );
      },
      selectedShoppingLists () {
        return this.mappedShoppingLists.filter(list => list.isSelected);
      },
      opened () {
        return this.modal.type === this.modalType;
      },
      productIds () {
        return !this.isMultiple
          ? [this.productId]
          : this.products.map((product) => {
            return product.variant.productId;
          });
      },
      productsImages () {
        const items = this.products.slice(0, 3);
        if (this.products.length > 3) {
          items.push({
            isPlaceholder: true,
            placeholderText: `+${this.products.length - 3}`,
          });
        }
        return items;
      },
      modalTitle () {
        if (this.isMultiple) {
          return this.$awt('aw.common.favourite.mobile_modal.multiple_title');
        }
        return this.showRemovedFromList ? this.$awt('aw.common.favourite.mobile_modal.remove_title') : this.$awt('aw.common.favourite.mobile_modal.title');
      },
    },
    watch: {
      opened () {
        this.localShoppingListData = !this.isMultiple
          ? [...this.shoppingListData]
          : this.calculateMultipleProductsShoppingListData()
        ;
      },
      shoppingListData: {
        handler (newValue) {
          this.localShoppingListData = newValue;
        },
        deep: true,
      },
    },
    methods: {
      calculateMultipleProductsShoppingListData () {
        return this.products.map((product) => {
          return product.variant.shoppingListsContain;
        }).reduce((acc, shoppingListsContain) => {
          return acc = [...shoppingListsContain];
        });
      },
      showAddNewShoppingListFormCallback () {
        this.showAddNewShoppingListForm = true;
        this.$refs.addNewShoppingListForm.showCallback();
      },
      async removeFromList (listId) {
        const productsStore = useProductsStore();
        let anyError = false;

        let requestData = [];
        if (!this.isMultiple) {
          requestData = [{
            objectId: this.variantId + '',
            quantity: {
              quantity: 0,
            },
          }];
        } else {
          requestData = this.products.map((product) => {
            return {
              objectId: product.variant.id + '',
              quantity: 0,
            };
          });
        }

        await this.$api.$put(`/me/shopping_lists/${listId}/product_variants`, { items: requestData })
          .catch((error) => {
            if (error?.response?.status === 400) {
              error.response.data.errors = [{
                ...error.response.data,
                message: '',
                hideErrorMsg: true,
              }];
              setErrorNotification({ nuxtApp: useNuxtApp(), error, closable: false,forceModal: true });
            } else {
              setErrorNotification({ nuxtApp: useNuxtApp(), error });
            }
            anyError = true;
            this.$logger.error(error);
          });

        // All OK: emit success
        if (!anyError) {
          this.localShoppingListData = this.localShoppingListData.filter(id => id !== listId);
          await productsStore.fetchUnCachedPartsReqAndUpdateStore({ productIds: this.productIds });
        }
      },
      async saveChanges () {
        const userInterfaceStore = useUserInterfaceStore();

        userInterfaceStore.startLoading({ id: 'save-add-to-saved-basket-modal' });

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const anyError = [];
        const userStore = useUserStore();

        const newElements = this.localShoppingListData.filter(listId => !this.shoppingListData.includes(listId));
        const deletedElements = this.shoppingListData.filter(listId => !this.localShoppingListData.includes(listId));

        let requestDatas = [];
        if (!this.isMultiple) {
          requestDatas = [{
            productId: this.productId + '',
            variantId: this.variantId + '',
            quantity: {
              quantity: this.count,
              type: {
                0: 'piece',
                1: 'weight',
              }[unitNames[this.selectedValue]],
            },
          }];
        } else {
          requestDatas = this.products.map((product) => {
            return {
              productId: product.variant.productId + '',
              variantId: product.variant.id + '',
              quantity: {
                quantity: product.quantity.quantity,
                type: product.quantity.type,
              },
            };
          });
        }

        for (const requestData of requestDatas) {
          const addElementPromise = !newElements.length
            ? null
            : this.$api.post(`products/${requestData.productId}/variants/${requestData.variantId}/shopping_lists`,
                             {
                               items: newElements,
                               quantity: requestData.quantity,
                             },
            );
          const deleteElementPromise = !deletedElements.length
            ? null
            : this.$api.put(`products/${requestData.productId}/variants/${requestData.variantId}/shopping_lists`,
                            {
                              items: deletedElements,
                              quantity: {
                                quantity: 0,
                              },
                            },
            );

          await Promise.all([addElementPromise, deleteElementPromise])
            .catch((error) => {
              if (error?.response?.status === 400) {
                error.response.data.errors = [{
                  ...error.response.data,
                  message: '',
                  hideErrorMsg: true,
                }];
                setErrorNotification({ nuxtApp: self, error, closable: false,forceModal: true });
              } else {
                setErrorNotification({ nuxtApp: self, error });
              }
              anyError.push(true);
              self.$logger.error(error);
            });
        }

        for (const listId of newElements) {
          userStore.addShoppingListToSessionData(listId);
          try {
            if ('variantId' in self.analyticsFavouriteGroupType) {
              if (!this.isMultiple) {
                self.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                  productData: this.productData,
                  variantProperty: 'selectedVariant',
                });
              } else {
                this.products.forEach((product) => {
                  self.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                    productData: product,
                    variantProperty: 'variant',
                  });
                });
              }
            }
          } catch (error) {
            self.$logger.error(error);
          }
        }
        for (const listId of deletedElements) {
          userStore.removeShoppingListFromSessionData(listId);
        }

        if (anyError.length === 0) {
          const productsStore = useProductsStore();
          await productsStore.fetchUnCachedPartsReqAndUpdateStore({ productIds: this.productIds });
        }
        this.closeModal();
        userInterfaceStore.endLoading({ id: 'save-add-to-saved-basket-modal' });
      },
      handleCheckboxChange (list, checked) {
        if (checked) {
          this.localShoppingListData.push(list.id);
        } else {
          this.localShoppingListData = this.localShoppingListData.filter(id => id !== list.id);
        }
      },
      closeModal () {
        const modalStore = useModalStore();
        if (this.$refs.addNewShoppingListForm) {
          this.$refs.addNewShoppingListForm.newShoppingListName.model = '';
        }
        this.showAddNewShoppingListForm = false;
        modalStore.hideFavModal({ modalType: this.modalType });
        this.$emit('modal-close');
      },
    },

  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.product {
  display: grid;
  grid-template-columns: 64px auto;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &Image {
    width: 48px;
    height: 48px;

    &Wrapper {
      width: 64px;
      height: 64px;
      padding: 8px;
      border-radius: 12px;
      background: $color-background-4;
    }
  }

  &Name {
    font-size: 16px;
    font-weight: $font-weight-bold-v2;
    line-height: 24px;
  }
}

.shoppingListItem {
  margin-bottom: 8px;
  padding: 6px 0;
}


.addNewShoppingListButton {
  margin: 2px 0;
}

.addShoppingListButton {
  /* no op */

  &Wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }
}

.removeFromListTitle {
  font-size: 14px;
  font-weight: $font-weight-bold-v2;
  line-height: 20px;
  margin-bottom: 8px;
}

.removeFromList {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  &Item {

    font-size: 14px;
    font-weight: $font-weight-normal-v2;
    font-style: normal;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    hyphens: auto;
    border-radius: 8px;
    gap: 8px;

    &Icon {
      cursor: pointer;
    }
  }
}

.emptyList {
  display: flex;
  align-items: center;
  justify-content: center;
}

.productsImageContainer {
  display: grid;
  grid-template-columns: repeat(4, 56px);
  gap: 14px;

  &Wrapper {
    display: grid;
    align-items: center;
    margin-top: 20px;
    padding-bottom: 36px;
    grid-template-columns: max-content 16px;
    gap: 14px;
  }
}

.productsImageItem {
  padding: 8px;
  border-radius: 12px;
  background: $color-background-2;


  &Placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    padding: 16px;
    color: $color-white;
    border-radius: 12px;
    background: $color-olive-750;
  }
}

</style>
